/**
 * StaleCoverageNotice — the research-staleness card (research-freshness spec
 * §C.6/§C.7), rendered beneath the assistant turn that earned it.
 *
 * The copy rule it enforces: the card states ONLY what the coverage row says —
 * the ticker, the company, and the three dates — and nothing the server does
 * not know. No earnings release, no release date, no claim the note is wrong:
 * `next_catalyst_date` having passed is the entire evidence, and saying more
 * would be inventing it.
 *
 * Two invariants the component owns:
 *  1. It is chip-independent. The agent's own question can demote the chip row
 *     (§C.4a), so the notice renders its own Refresh button and never reads the
 *     chips to decide whether it is actionable.
 *  2. `refresh_attempted` (§C.5) is the annotated state: the user already spent
 *     a run on this note and the gate said it did not advance, so the button is
 *     off — the offer would restart the tap-loop the gate exists to close. Its
 *     `reason`/`at`/`from_version` are server bookkeeping and are not rendered.
 *
 * It carries no rating, price target or recommendation level: those fields are
 * dropped in `stale_coverage.py` before the payload is built, and this
 * component has no prop that could render them (compliance hard rule).
 */
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

/** The `refresh_attempted` blob (§C.5). Kept as opaque bookkeeping: only its
 * presence changes what the notice says. */
export interface StaleCoverageAttempted {
  at?: string | null;
  reason?: string | null;
  from_version?: number | null;
}

/** The card payload the backend writes at `metadata.stale_coverage_card` — the
 * four facts plus `version`, which the dismissal receipt is matched against.
 * Every field is required and nullable: the transcript reader normalises an
 * absent value to null, so a missing one is a fact the row does not carry,
 * never a field the notice can silently omit. */
export interface StaleCoverageCard {
  ticker: string;
  name: string | null;
  research_date: string | null;
  current_reporting_period: string | null;
  next_catalyst_date: string | null;
  version: number | null;
  refresh_attempted?: StaleCoverageAttempted | null;
}

export function StaleCoverageNotice({
  card,
  onRefresh,
  onDismiss,
}: {
  card: StaleCoverageCard | null;
  onRefresh: (ticker: string) => void;
  onDismiss: () => void;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  if (!card) return null;

  const subject = card.name ? `${card.ticker} — ${card.name}` : card.ticker;
  // Each fact segment appears only when the row carries it — a coverage record
  // written before the period/catalyst columns existed still says the note is
  // old, it just says less.
  const facts = [
    card.research_date
      ? t('chat.staleCoverage.lastResearched', {
          defaultValue: 'Last researched {{date}}',
          date: card.research_date,
        })
      : null,
    card.current_reporting_period
      ? t('chat.staleCoverage.covering', {
          defaultValue: 'covering {{period}}',
          period: card.current_reporting_period,
        })
      : null,
    card.next_catalyst_date
      ? t('chat.staleCoverage.nextCatalyst', {
          defaultValue: 'next catalyst was due {{date}}',
          date: card.next_catalyst_date,
        })
      : null,
  ].filter(Boolean);
  const annotated = card.refresh_attempted != null;

  return (
    <View
      testID="stale-coverage-notice"
      style={[
        styles.card,
        {
          backgroundColor: theme.colors.surface,
          borderColor: theme.colors.border,
        },
      ]}
    >
      <View style={styles.head}>
        <Text style={[styles.title, { color: theme.colors.text }]}>
          {t('chat.staleCoverage.title', { defaultValue: 'Research note is out of date' })}
        </Text>
        <Pressable
          testID="stale-coverage-dismiss"
          onPress={onDismiss}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel={t('actions.dismiss', { defaultValue: 'Dismiss' })}
          style={styles.dismiss}
        >
          <Text style={[styles.dismissText, { color: theme.colors.textTertiary }]}>✕</Text>
        </Pressable>
      </View>
      <Text style={[styles.subject, { color: theme.colors.textSecondary }]}>{subject}</Text>
      {facts.length > 0 ? (
        <Text style={[styles.facts, { color: theme.colors.textSecondary }]}>
          {facts.join(' · ')}
        </Text>
      ) : null}
      {annotated ? (
        <Text style={[styles.stalled, { color: theme.colors.textTertiary }]}>
          {t('chat.staleCoverage.stalled', {
            defaultValue:
              "The refresh ran but the research note's next catalyst date did not move. It will be picked up by the Saturday job.",
          })}
        </Text>
      ) : (
        <Pressable
          testID="stale-coverage-refresh"
          onPress={() => onRefresh(card.ticker)}
          accessibilityRole="button"
          style={[styles.refresh, { backgroundColor: theme.colors.primary }]}
        >
          <Text style={[styles.refreshText, { color: theme.colors.textInverse }]}>
            {t('chat.staleCoverage.refresh', { defaultValue: 'Refresh the research' })}
          </Text>
        </Pressable>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    card: {
      borderWidth: 1,
      borderRadius: theme.radii.lg,
      padding: theme.spacing.md,
      marginTop: theme.spacing.xs,
      gap: theme.spacing.xs,
    },
    head: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
    },
    title: {
      flex: 1,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
    },
    subject: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    facts: {
      fontSize: theme.typography.fontSizeSm,
    },
    stalled: {
      fontSize: theme.typography.fontSizeSm,
    },
    refresh: {
      alignSelf: 'flex-start',
      borderRadius: theme.radii.sm,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs,
      marginTop: theme.spacing.xxs,
    },
    refreshText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    dismiss: {
      paddingHorizontal: theme.spacing.xs,
      paddingVertical: theme.spacing.xxs,
    },
    dismissText: {
      fontSize: theme.typography.fontSizeSm,
    },
  });
}
